import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTutor } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Avatar } from "../components/Avatar";
import { CallAnalysisPanel } from "../components/call/CallAnalysisPanel";
import { fmtDuration } from "../lib/format";
import { toast } from "sonner";

const LIVE_PHONEMES = ["/θ/", "/ð/", "/r/", "/æ/", "/ə/", "/ʃ/", "/tʃ/"];

const nextWaveBar = () => ({
  id: `wave-${Date.now()}-${Math.random()}`,
  height: 30 + Math.random() * 70,
});

const nextPhonemeReading = () => {
  const phoneme = LIVE_PHONEMES[Math.floor(Math.random() * LIVE_PHONEMES.length)];
  return { label: phoneme, score: 55 + Math.floor(Math.random() * 45) };
};

const createDemoSessionId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `demo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default function CallPage() {
  const { id } = useParams();
  const [tutor, setTutor] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [livePhoneme, setLivePhoneme] = useState({ label: "/r/", score: 92 });
  const [ending, setEnding] = useState(false);
  const [wave, setWave] = useState(() => (
    Array.from({ length: 60 }, (_, index) => ({
      id: `wave-${index}`,
      height: Math.random() * 100,
    }))
  ));
  const { state, setLastSession } = useApp();
  const navigate = useNavigate();

  const loadTutor = useCallback(async () => {
    try {
      const nextTutor = await getTutor(id);
      setTutor(nextTutor);
    } catch (error) {
      toast.error("Tutor no encontrado");
      navigate("/tutores");
    }
  }, [id, navigate]);

  useEffect(() => {
    loadTutor();
  }, [loadTutor]);

  useEffect(() => {
    if (!tutor) return;
    const tick = setInterval(() => {
      setSeconds((s) => s + 1);
      setLivePhoneme(nextPhonemeReading());
      setWave((previousWave) => [...previousWave.slice(1), nextWaveBar()]);
    }, 1000);
    return () => clearInterval(tick);
  }, [tutor]);

  const endCall = async () => {
    if (ending || !tutor) return;
    setEnding(true);
    const finalSeconds = Math.max(1, seconds);
    const finalCents = 2200;
    // Randomized scores for demo
    const fluency = 65 + Math.floor(Math.random() * 25);
    const pron = 60 + Math.floor(Math.random() * 30);
    const strongest = LIVE_PHONEMES[Math.floor(Math.random() * LIVE_PHONEMES.length)];
    let weakest = LIVE_PHONEMES[Math.floor(Math.random() * LIVE_PHONEMES.length)];
    while (weakest === strongest) weakest = LIVE_PHONEMES[Math.floor(Math.random() * LIVE_PHONEMES.length)];

    const saved = {
      id: createDemoSessionId(),
      tutor_id: tutor.id,
      tutor_display_name: tutor.display_name,
      tutor_country: tutor.country,
      tutor_accent: tutor.accent,
      student_name: state.user?.name || "Invitado",
      duration_seconds: finalSeconds,
      amount_cents: finalCents,
      fluency_score: fluency,
      pronunciation_score: pron,
      strongest_phoneme: strongest,
      weakest_phoneme: weakest,
      outcome: "completada",
      created_at: new Date().toISOString(),
    };
    setLastSession(saved);
    navigate(`/post-clase/${saved.id}`);
  };

  if (!tutor) {
    return (
      <div className="min-h-[calc(100vh-68px)] flex items-center justify-center">
        <div className="wk-skel" style={{ width: 240, height: 24 }} />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-68px)]" style={{ background: "#1E1924", color: "#FAF9FB" }}>
      {/* Top bar */}
      <div className="border-b" style={{ borderColor: "rgba(250,249,251,0.08)" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="wk-live-dot" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-medium" style={{ color: "#7228C8" }}>En llamada</span>
            <span className="hidden sm:inline text-[13px]" style={{ color: "rgba(250,249,251,0.55)" }}>
              con {tutor.display_name} · {tutor.accent}
            </span>
          </div>
          <div className="flex items-center gap-6 sm:gap-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "rgba(250,249,251,0.5)" }}>Duración</div>
              <div className="font-mono-num text-[24px] lg:text-[28px] font-bold tracking-hero" data-testid="call-timer">{fmtDuration(seconds)}</div>
            </div>
            <button onClick={endCall} disabled={ending} className="wk-btn wk-btn-primary" data-testid="call-end-btn">
              {ending ? "Guardando…" : "Finalizar sesión"}
            </button>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
          {/* Tutor camera */}
          <div
            className="rounded-2xl overflow-hidden relative"
            style={{ background: "#0e0b12", aspectRatio: "16/10", minHeight: 340, border: "1px solid rgba(250,249,251,0.08)" }}
          >
            {/* Ambient */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(500px 300px at 30% 40%, rgba(114,40,200,0.35), transparent 70%), radial-gradient(400px 300px at 80% 70%, rgba(114,40,200,0.18), transparent 70%)",
              }}
            />
            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.14em] font-medium px-2 py-1 rounded" style={{ background: "rgba(250,249,251,0.14)" }}>
                {tutor.country_code}
              </span>
              <span className="text-[13px] font-medium">{tutor.display_name}</span>
            </div>
            <div className="absolute top-5 right-5 wk-wave" aria-hidden>
              <span /><span /><span /><span /><span /><span />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Avatar initials={tutor.initials} color={tutor.avatar_color} size={112} ring />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
              <div>
                <div className="text-[12px]" style={{ color: "rgba(250,249,251,0.55)" }}>Simulación de videollamada · sin conexión de vídeo en esta fase</div>
              </div>
              {/* Self cam */}
              <div
                className="w-28 h-20 lg:w-40 lg:h-28 rounded-lg flex items-center justify-center relative overflow-hidden"
                style={{ background: "#252030", border: "1px solid rgba(250,249,251,0.1)" }}
              >
                <Avatar initials={(state.user?.name || "T").slice(0, 2).toUpperCase()} color="#7228C8" size={44} />
                <div className="absolute bottom-1.5 right-1.5 wk-wave" style={{ height: 14 }}>
                  <span style={{ height: 4 }} /><span style={{ height: 10 }} /><span style={{ height: 6 }} /><span style={{ height: 12 }} />
                </div>
              </div>
            </div>
          </div>

          <CallAnalysisPanel
            phonemes={LIVE_PHONEMES}
            livePhoneme={livePhoneme}
            wave={wave}
          />
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
          <div className="text-[12px]" style={{ color: "rgba(250,249,251,0.55)" }}>
            Sesión incluida en el Pack Prueba de Pronunciación · $22.
          </div>
          <button onClick={endCall} disabled={ending} className="wk-btn wk-btn-ghost" style={{ borderColor: "rgba(250,249,251,0.25)", color: "#FAF9FB" }} data-testid="call-end-btn-2">
            {ending ? "Guardando…" : "Colgar"}
          </button>
        </div>
      </div>
    </div>
  );
}
